import type { AuthScope } from "@playbookai/types";
import type { IdTokenClaims } from "./auth-store.js";

/* The identity that a piece of in-progress admin state belongs to.

   A tenant alone is not enough. Two administrators of the SAME tenant can
   replace one another's session in place — the `/admin` route tree never
   unmounts across a `/me` refetch — which leaves the first one's draft, selected
   row, or shown credential on screen for the second to act on.

   The shared `AuthScope`, rather than a local look-alike: it is exactly these
   three fields, `publishAuthScope` (auth-scope.ts) builds it from the same
   claims, and a duplicate shape would be free to drift from it. `null` is a
   signed-out or claimless render, which is also how `authScopeSource` reports
   one.

   Lifted out of OrganizationAdmin (PB-552) when the invite workflow needed the
   same fence (PB-1597). One copy on purpose — this predicate decides whether a
   credential may still be displayed, so two copies free to diverge is the last
   thing it should be. */
export type SessionScope = AuthScope | null;

export function scopeFromClaims(claims: IdTokenClaims | null): SessionScope {
  if (!claims?.tenantId || !claims.userId || !claims.authSessionGeneration) {
    return null;
  }
  return {
    tenantId: claims.tenantId,
    actorUserId: claims.userId,
    authSessionGeneration: claims.authSessionGeneration,
  };
}

/* Two claimless renders are deliberately NOT the same session: there is no
   identity to match, so nothing may be carried across one. */
export function sameSession(left: SessionScope, right: SessionScope): boolean {
  return (
    left !== null &&
    right !== null &&
    left.tenantId === right.tenantId &&
    left.actorUserId === right.actorUserId &&
    left.authSessionGeneration === right.authSessionGeneration
  );
}

/* ── Using this correctly: a scope is not an identity ──────────────────────────

   Three review rounds on PB-1597 all landed on the same misuse, so it is written
   down here rather than rediscovered a fourth time.

   A scope fence at RENDER time answers "may this be displayed". It does not
   answer "may this be written", and async completions write:

     setDialogState((cur) => (cur ? { ...cur, ...result } : cur));   // WRONG

   That patches whichever container is current. If the current one is a different
   container that happens to be live — the replacement administrator's, or the
   same admin's after a close and reopen — the render fence renders the foreign
   write without complaint. On this page that meant one administrator's show-once
   invite URL appearing in another's dialog.

   So: give every opened container an id that is unique per open, carry it in the
   mutation's variables, and have the completion address that instance.

     setDialogState((cur) =>
       cur && cur.id === variables.id && sameSession(cur.scope, variables.scope)
         ? { ...cur, ...result }
         : cur,
     );

   The id is the load-bearing half — a close-and-reopen inside ONE session yields
   an identical scope, and dialogs here can be dismissed mid-flight by Escape or
   a header close button, neither of which is gated on the pending state. Do not
   assume "the container is gone by then"; it may have been replaced.

   Both sites this note used to log as follow-ups are now closed (PB-1562), and
   they closed differently — which is the more useful half of the record:

   - MembersAdmin's local-account dialog now uses the id + scope form above. Its
     credential no longer survives a handover by accident of a stale stamping
     closure; the dialog id is carried in the mutation's variables and the
     completion addresses that instance. The id is load-bearing there for a
     second reason too: it keys the map that hands the response from `mutationFn`
     to `onSuccess`, because two creations can genuinely overlap.

   - OrganizationAdmin's `setDraft(null)` did NOT need an operation id. The race
     this note asserted is unreachable: every field is `disabled={saving}`, so
     there is no second draft for a settled save to discard. An operation id was
     implemented, found to guard nothing, and reverted; a test pins the gate that
     actually holds instead ("cannot be edited while a save is in flight").

   The lesson worth keeping from that pair: check whether the racy state is
   reachable before reaching for an id. A separate gate — a pending guard, a
   disabled field — can already make it unrepresentable, and an id added on top
   of one buys complexity rather than safety. Where no such gate exists, or where
   the value is a show-once credential, the id is still the answer. */
